import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  updateFormField,
  togglePasswordVisibility,
  handlePopup,
} from "./redux/userSlice";
import { createTeacherUser, fetchOrgUsersById } from "./redux/userThunk";
import userStore from "../../zustand/userStore";
import { getMasterDataByUserId } from "../masterData/redux/masterDataThunk";
import { fetchUnAssignTeachers } from "../../features/SchoolModule/teachers/redux/teacherSlice";
import LoaderOverlay from "../LoaderOverlay";

const ExistingUser = () => {
  const dispatch = useDispatch();
  const { formData, status, showPassword, showConfirmPassword } = useSelector(
    (state) => state.user
  );

  const userData = userStore((state) => state.user);
  const { allteachers: teachers } = useSelector((state) => state.teachers);
  const { school_id } = useSelector((state) => state.masterData || {});

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value }));
  };

  useEffect(() => {
    dispatch(getMasterDataByUserId(userData?.id));
  }, [dispatch, userData?.id]);

  useEffect(() => {
    if (school_id) {
      dispatch(fetchUnAssignTeachers());
    }
  }, [dispatch, school_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    if (!/^\d{10}$/.test(formData.contact_number)) {
      alert("Contact number must be exactly 10 digits");
      return;
    }

    const selectedTeacher = teachers.find(
      (t) => t.teacher_id === Number(formData.teacher_id)
    );

    const body = {
      username: selectedTeacher?.name || "",
      email: formData.email,
      password: formData.password,
      role_id: formData.role_id,
      created_by: userData.id,
      school_name: userData.school_name,
      contact_number: formData.contact_number,
      address: userData.address,
      school_type: "Primary",
      db_name: userData.db_name,
      port: userData.port,
      account_type: userData.account_type,
      teacher_id: formData.teacher_id,
    };

    try {
      await dispatch(createTeacherUser(body)).unwrap();
      alert("User created successfully");
      dispatch(fetchOrgUsersById());
      dispatch(handlePopup(false));
    } catch (err) { 
      alert(err || "Failed to create user");
    }
  };

  return (
    <>
      <AnimatePresence>
        {status === "loading" && <LoaderOverlay />}
      </AnimatePresence>
      <motion.div
        className="container mt-3"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mx-auto" style={{ maxWidth: "700px" }}>
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Teacher */}
              <div className="col-md-6">
                <label className="form-label">Teacher</label>
                <select
                  className="form-select"
                  value={formData.teacher_id || ""}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "teacher_id", value: e.target.value },
                    })
                  }
                  required
                >
                  <option value="">Select a teacher</option>
                  {teachers?.length ? (
                    teachers.map((t) => (
                      <option key={t.teacher_id} value={t.teacher_id}>
                        {t.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No teachers found</option>
                  )}
                </select>
              </div>

              {/* Email */}
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Password */}
              <div className="col-md-6">
                <label className="form-label">Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-control"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="input-group-text"
                    onClick={() =>
                      dispatch(togglePasswordVisibility("showPassword"))
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <i
                      className={`fa-solid ${
                        showPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    />
                  </span>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="col-md-6">
                <label className="form-label">Confirm Password</label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    className="form-control"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                  <span
                    className="input-group-text"
                    onClick={() =>
                      dispatch(togglePasswordVisibility("showConfirmPassword"))
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <i
                      className={`fa-solid ${
                        showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                      }`}
                    />
                  </span>
                </div>
              </div>
              {/* Contact Number */}
              <div className="col-md-6">
                <label className="form-label">Contact Number</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.contact_number || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      dispatch(
                        updateFormField({
                          field: "contact_number",
                          value,
                        })
                      );
                    }
                  }}
                  maxLength={10}
                  required
                />
              </div>

              {/* Role */}
              <div className="col-md-6">
                <label className="form-label">Role</label>
                <select
                  name="role_id"
                  className="form-select"
                  value={formData.role_id}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select Role
                  </option>
                  <option value={2}>Admin</option>
                  <option value={1}>User</option>
                </select>
              </div>
            </div>

            <div className="text-center mt-4">
              <button type="submit" className="btn btn-primary px-5">
                Submit
              </button>
            </div>
          </form>
        </div>
      </motion.div>{" "}
    </>
  );
};

export default ExistingUser;
